import React from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { agentKeys } from "../queryKeys";

interface TimeSeriesPoint {
  date: string;
  count: number;
}

interface AgentCardChartProps {
  agentId: string;
}

export function AgentCardChart({ agentId }: AgentCardChartProps) {
  const { data: rawData = [], isLoading: loading } = useQuery({
    queryKey: agentKeys.timeSeries(agentId, { metric: "messages", groupBy: "1d", range: "7d" }),
    queryFn: () =>
      apiFetch<{ data: TimeSeriesPoint[] }>(
        `/api/agents/${agentId}/stats/time-series?metric=messages&group_by=1d&range=7d`
      ).then((r) => r.data ?? []),
  });
  const data = rawData;

  if (loading) {
    return (
      <div className="agent-card-chart" aria-hidden>
        <svg className="agent-card-chart-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
          <line x1="0" y1="20" x2="200" y2="20" stroke="var(--border)" strokeWidth="1" strokeDasharray="4 2" />
        </svg>
      </div>
    );
  }

  const points = data.length > 0 ? data : [];
  const maxCount = Math.max(...points.map((d) => d.count), 1);
  const width = 200;
  const height = 40;
  const padding = 2;

  const getX = (i: number) => padding + (i / Math.max(points.length - 1, 1)) * (width - padding * 2);
  const getY = (count: number) => height - padding - (count / maxCount) * (height - padding * 2);

  const pathD =
    points.length === 0
      ? `M ${padding} ${height - padding} L ${width - padding} ${height - padding}`
      : points.length === 1
        ? `M ${padding} ${getY(points[0].count)} L ${width - padding} ${getY(points[0].count)}`
        : points.map((p, i) => `${i === 0 ? "M" : "L"} ${getX(i)} ${getY(p.count)}`).join(" ");

  return (
    <div className="agent-card-chart" aria-hidden>
      <svg
        className="agent-card-chart-svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      >
        <path
          d={pathD}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
